import React, { useState } from 'react';
import { Check, Clock, Copy, TriangleAlert } from 'lucide-react';
import { toast } from '@/components/toast/toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { OnboardingStatus } from '@/entities/onboarding-status';
import {
	ALLOW_NEW_REQUEST_WHILE_IN_REVIEW,
	buildSupportMailto,
	describeProcess,
} from './onboarding-in-review';

interface OnboardingInReviewProps {
	status: OnboardingStatus;
	// Continues into the new-certificate flow; the request in review stays.
	onStartNew: () => void;
	// Leaves the wizard (to the dashboard).
	onBack: () => void;
}

const OnboardingInReview: React.FC<OnboardingInReviewProps> = ({
	status,
	onStartNew,
	onBack,
}) => {
	const details = describeProcess(status.process);
	const [copied, setCopied] = useState(false);

	const copyRequestNumber = async () => {
		try {
			await navigator.clipboard.writeText(status.onboarding_ulid);
			setCopied(true);
			setTimeout(() => setCopied(false), 2_000);
		} catch {
			toast({
				mode: 'caution',
				description:
					'No pudimos copiar el número. Cópialo manualmente.',
			});
		}
	};

	return (
		<div
			data-testid="onboarding-in-review"
			style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				<Clock aria-hidden="true" />
				<h5>Tu solicitud está en revisión</h5>
			</div>
			<p>
				Estamos revisando tu verificación de identidad. Te avisaremos
				por correo cuando tu firma esté lista.
			</p>

			<dl
				style={{
					display: 'grid',
					gridTemplateColumns: 'max-content 1fr',
					gap: '8px 16px',
					alignItems: 'center',
				}}
			>
				<dt style={{ fontWeight: 600 }}>Número de solicitud</dt>
				<dd style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
					<code
						data-testid="request-number"
						style={{
							fontFamily: 'monospace',
							wordBreak: 'break-all',
						}}
					>
						{status.onboarding_ulid}
					</code>
					<Button
						variant="ghost"
						size="icon"
						// Text-height, so this row is no taller than the others.
						className="size-6"
						aria-label={
							copied
								? 'Número de solicitud copiado'
								: 'Copiar número de solicitud'
						}
						title={copied ? 'Copiado' : 'Copiar'}
						onClick={copyRequestNumber}
					>
						{copied ? <Check /> : <Copy />}
					</Button>
				</dd>
				{details.map(({ label, value }) => (
					<React.Fragment key={label}>
						<dt style={{ fontWeight: 600 }}>{label}</dt>
						<dd>{value}</dd>
					</React.Fragment>
				))}
				<dt style={{ fontWeight: 600 }}>Estado</dt>
				<dd>En revisión</dd>
			</dl>

			<p>
				{!ALLOW_NEW_REQUEST_WHILE_IN_REVIEW &&
					'Podrás iniciar una nueva solicitud cuando esta se resuelva. '}
				Si necesitas cambiar o cancelar esta solicitud, escríbenos a
				soporte.
			</p>
			<Button asChild>
				{/* Inline color: the global `a` rule (App.css) outranks the
				    button's text utility and painted the label blue on blue. */}
				<a
					href={buildSupportMailto(status.onboarding_ulid)}
					style={{ color: 'var(--primary-foreground)' }}
				>
					Contactar soporte
				</a>
			</Button>

			{ALLOW_NEW_REQUEST_WHILE_IN_REVIEW && (
				<>
					<Alert>
						<TriangleAlert />
						<AlertTitle>
							Ya tienes una solicitud en revisión
						</AlertTitle>
						<AlertDescription>
							Si inicias una nueva, se creará una segunda
							solicitud, independiente de esta y con su propio
							pago. Los pagos no son reembolsables.
						</AlertDescription>
					</Alert>
					<Button variant="secondary" onClick={onStartNew}>
						Iniciar una nueva solicitud
					</Button>
				</>
			)}

			<Button variant="secondary" onClick={onBack}>
				Regresar
			</Button>
		</div>
	);
};

export default OnboardingInReview;
